/**
 * Runtime configuration (docs/modules/infra.md): `/config.js` injects `window.__WINDROP_CONFIG__` at container
 * start; `import.meta.env.VITE_*` is the dev fallback. Domains are activation inputs (D13) and never baked in.
 */
export type AppEnv = 'dev' | 'staging' | 'prod';

export interface RuntimeConfig {
  /** Origin of the API. Empty string = same origin (the Vite dev proxy forwards `/v1`). */
  apiOrigin: string;
  webOrigin: string;
  consoleOrigin: string;
  buildSha: string;
  appEnv: AppEnv;
}

/** `/config.js` (infra/docker/nginx-spa.conf.template) writes camelCase keys; the env-style spelling is kept too. */
interface RawConfig {
  API_ORIGIN?: string;
  WEB_ORIGIN?: string;
  CONSOLE_ORIGIN?: string;
  BUILD_SHA?: string;
  APP_ENV?: string;
  apiOrigin?: string;
  webOrigin?: string;
  consoleOrigin?: string;
  buildSha?: string;
  appEnv?: string;
}

declare global {
  interface Window {
    __WINDROP_CONFIG__?: RawConfig;
  }
}

const stripSlash = (s: string): string => s.replace(/\/$/, '');

const asEnv = (value: string | undefined, fallback: AppEnv): AppEnv =>
  value === 'dev' || value === 'staging' || value === 'prod' ? value : fallback;

let cached: RuntimeConfig | null = null;

export function readRuntimeConfig(): RuntimeConfig {
  if (cached) return cached;
  const injected: RawConfig = (typeof window !== 'undefined' && window.__WINDROP_CONFIG__) || {};
  const env = import.meta.env;
  const pick = (a: string | undefined, b: string | undefined): string | undefined =>
    a && a.length ? a : b && b.length ? b : undefined;
  cached = {
    apiOrigin: stripSlash(pick(injected.apiOrigin, injected.API_ORIGIN) ?? env.VITE_API_ORIGIN ?? ''),
    webOrigin: stripSlash(
      pick(injected.webOrigin, injected.WEB_ORIGIN) ?? env.VITE_WEB_ORIGIN ?? 'http://localhost:5180',
    ),
    consoleOrigin: stripSlash(
      pick(injected.consoleOrigin, injected.CONSOLE_ORIGIN) ??
        env.VITE_CONSOLE_ORIGIN ??
        'http://localhost:5181',
    ),
    buildSha: pick(injected.buildSha, injected.BUILD_SHA) ?? env.VITE_BUILD_SHA ?? 'dev',
    appEnv: asEnv(pick(injected.appEnv, injected.APP_ENV) ?? env.VITE_APP_ENV, env.DEV ? 'dev' : 'prod'),
  };
  return cached;
}

/** Test hook: forget the cached config so a test can inject its own `window.__WINDROP_CONFIG__`. */
export function resetRuntimeConfigForTests(): void {
  cached = null;
}

export const isDevBuild = (): boolean => Boolean(import.meta.env.DEV);
